import { TFunction } from 'next-i18next'
import { RegisterOptions } from 'react-hook-form'
import { DropdownFieldSchema, DropdownItems, FieldType, InputFieldSchema, MoneyFieldSchema, Schema } from '@afterpaytouch/form'
import { ExpenseDetailsForm, ExpenseSchemaUpdates, GetSchemaAndFormDataResult, IncomeDetailsForm, MaxFacilityForm } from './types'
import { amountValidation, requiredValidation } from './validations'
import { EmploymentStatus, HouseholdDependants, IncomeFrequency, IncomeSourceCategory, ResidentialStatus } from '@afterpaytouch/portal-api'
import { Ref } from 'react'
import { Money } from '@afterpay/types'

export interface IncomeSchemaUpdates {
  frequency?: string
  showOtherIncomeFields: boolean
  isValidStatus: boolean
}
interface InputFieldSchemaOpts {
  label: string
  value?: string
  options?: RegisterOptions
  inline?: boolean | number
  otherProps?: any
  error?: boolean
  show?: boolean
}
interface MoneyFieldSchemaOpts {
  label: string
  value?: string
  options?: RegisterOptions
  inline?: boolean | number
  otherProps?: any
  error?: boolean
  show?: boolean
  currencySymbol?: string
  ariaDescribedBy?: string
}

interface DropdownSchemaOpts {
  label: string
  value?: string
  items: DropdownItems[] | null
  options: RegisterOptions
  otherProps?: any
  id?: string
  error?: boolean
  show?: boolean
}

export const getMoneyFieldSchema = ({
  value = '',
  label,
  inline = false,
  options = {},
  otherProps = {},
  error = false,
  show = true,
  currencySymbol,
  ariaDescribedBy,
}: MoneyFieldSchemaOpts): MoneyFieldSchema => ({
  type: FieldType.Money,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
    'aria-describedby': ariaDescribedBy,
  },
  currencySymbol,
  inline,
  options,
})

export const getInputFieldSchema = ({
  value = '',
  label,
  inline = false,
  options = {},
  otherProps = {},
  error = false,
  show = true,
}: InputFieldSchemaOpts): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  inline,
  options,
})

export const getDropdownFieldSchema = ({
  label,
  value = '',
  items,
  options,
  id,
  error = false,
  show = true,
  otherProps,
}: DropdownSchemaOpts): DropdownFieldSchema => ({
  type: FieldType.Dropdown,
  props: {
    label,
    defaultValue: value,
    id,
    error,
    ...otherProps,
  },
  items: items ?? [],
  options,
  show,
})

export const orderedEmploymentStatus = [
  EmploymentStatus.FullTime,
  EmploymentStatus.PartTime,
  EmploymentStatus.Casual,
  EmploymentStatus.SelfEmployed,
  EmploymentStatus.NotEmployed,
]

export const orderedIncomeFrequency = [IncomeFrequency.WEEKLY, IncomeFrequency.FORTNIGHTLY, IncomeFrequency.MONTHLY]

export const orderedResidentialStatus = [ResidentialStatus.REN, ResidentialStatus.HMO, ResidentialStatus.HNM, ResidentialStatus.LFP, ResidentialStatus.SAC]

export const orderedDependants = [HouseholdDependants.NONE, HouseholdDependants.ONE, HouseholdDependants.TWO, HouseholdDependants.THREE_PLUS]

export const orderedIncomeCategories = [IncomeSourceCategory.Salary, IncomeSourceCategory.Benefits, IncomeSourceCategory.ChildSupport]

const calculateMaximumCreditFacilityLimit = (facilityLimitAmount: string, orderAmount: string, formatMoney: (amount?: string) => string): DropdownItems[] => {
  const dropdownIncrement = 1000
  const lowValue = Number(orderAmount)
  const highValue = Number(facilityLimitAmount)

  if (isNaN(lowValue) || isNaN(highValue)) {
    return []
  }

  const highSteps = Math.ceil(highValue / dropdownIncrement)
  const lowSteps = Math.floor(lowValue / dropdownIncrement)
  const noOfSteps = highSteps - lowSteps

  const dropdown: DropdownItems[] = Array.from({ length: noOfSteps }, (_, index) => {
    const incrementAmount = String((index + 1 + lowSteps) * dropdownIncrement)
    return {
      key: incrementAmount,
      label: formatMoney(incrementAmount),
    }
  })

  // Add the original amount to the bottom of the dropdown list
  const dropdownWithOriginalAmount: DropdownItems[] = [
    {
      key: orderAmount ?? '',
      label: formatMoney(orderAmount),
    },
  ]
    .concat(dropdown)
    .reverse()

  return dropdownWithOriginalAmount
}

export const generateCreditLimitList = (
  orderAmount: Money | undefined,
  facilityLimit: Money | undefined,
  formatMoney: (amount?: string) => string
): DropdownItems[] => {
  if (typeof orderAmount === 'undefined' || typeof facilityLimit === 'undefined') {
    return []
  }

  return calculateMaximumCreditFacilityLimit(facilityLimit?.amount, orderAmount?.amount, formatMoney)
}

export const getMaxLimitSchema = (components: any, t: TFunction, creditLimitList: DropdownItems[]): GetSchemaAndFormDataResult<MaxFacilityForm> => {
  const required = requiredValidation(t)

  const schema: Schema<MaxFacilityForm> = {
    maxFacilityLimit: getDropdownFieldSchema({
      id: 'credit-limit-dropdown-field',
      label: t('consumerLending:employmentDetails.formLabels.creditLimit.label'),
      value: creditLimitList[0]?.key,
      options: {
        ...required('creditLimit'),
      },
      items: creditLimitList,
    }),
    creditLimitText: {
      type: FieldType.Component,
      Component: components.creditLimitText,
    },
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }
  return { schema }
}

export const generateDropdownItems = (t: TFunction, items: any, name: string): DropdownItems[] => {
  const dropdownItems = Object.keys(items).map((key) => {
    return {
      key: items[key],
      label: t(`consumerLending:affordability.${name}.${items[key].toLowerCase()}`),
    }
  })

  return dropdownItems
}

export const getExpensechema = (
  components: any,
  t: TFunction,
  schemaUpdates: ExpenseSchemaUpdates,
  residentialStatusOptions: DropdownItems[],
  householdDependantsOptions: DropdownItems[],
  iconClickHandler: (string) => void,
  otherLoansRef?: Ref<HTMLElement>,
  livingExpensesRef?: Ref<HTMLElement>,
  housingCostsRef?: Ref<HTMLElement>,
  symbol?: string
): GetSchemaAndFormDataResult<ExpenseDetailsForm> => {
  const required = requiredValidation(t)
  const checkExpenses = amountValidation(t)
  const { showExtraFields } = schemaUpdates

  const schema: Schema<ExpenseDetailsForm> = {
    residentialStatus: getDropdownFieldSchema({
      label: t('consumerLending:affordability.residentialStatus.label'),
      id: 'residential-dropdown',
      options: {
        ...required('residentialStatus'),
      },
      items: residentialStatusOptions,
    }),
    noOfDependents: getDropdownFieldSchema({
      label: t('consumerLending:affordability.householdDependants.label'),
      id: 'household-dependants-dropdown',
      options: {
        ...required('householdDependants'),
      },
      items: householdDependantsOptions,
      show: showExtraFields,
    }),
    monthlyHousingCosts: getMoneyFieldSchema({
      label: t('consumerLending:affordability.housingCosts.label'),
      options: {
        ...required('housingCosts'),
        ...checkExpenses(true, 'housingCosts'),
      },
      show: showExtraFields,
      otherProps: {
        icon: 'Info',
        iconClickHandler: () => iconClickHandler('housingCosts'),
        iconRef: housingCostsRef,
      },
      currencySymbol: symbol,
      ariaDescribedBy: 'housing-costs-tooltip',
    }),
    otherMonthlyLoanRepayments: getMoneyFieldSchema({
      label: t('consumerLending:affordability.otherLoans.label'),
      options: {
        ...required('otherLoans'),
        ...checkExpenses(true, 'otherLoans'),
      },
      show: showExtraFields,
      otherProps: {
        icon: 'Info',
        iconClickHandler: () => iconClickHandler('otherLoans'),
        iconRef: otherLoansRef,
      },
      currencySymbol: symbol,
      ariaDescribedBy: 'other-loans-tooltip',
    }),
    monthlyLivingExpenses: getMoneyFieldSchema({
      label: t('consumerLending:affordability.livingExpenses.label'),
      options: {
        ...required('livingExpenses'),
        ...checkExpenses(true, 'livingExpenses'),
      },
      show: showExtraFields,
      otherProps: {
        icon: 'Info',
        iconClickHandler: () => iconClickHandler('livingExpenses'),
        iconRef: livingExpensesRef,
      },
      currencySymbol: symbol,
      ariaDescribedBy: 'living-expenses-tooltip',
    }),
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }
  return { schema }
}

export const getIncomeSchema = (
  components: any,
  t: TFunction,
  employmentStatusList: DropdownItems[],
  incomeFrequencyList: DropdownItems[],
  schemaUpdates: IncomeSchemaUpdates,
  otherIncomeRef?: Ref<HTMLElement>,
  handleIconClicked?: () => void,
  symbol?: string
): GetSchemaAndFormDataResult<IncomeDetailsForm> => {
  const { frequency, showOtherIncomeFields, isValidStatus } = schemaUpdates
  const required = requiredValidation(t)
  const checkIncome = amountValidation(t)
  const isRequired = (name: string): RegisterOptions | null => {
    return isValidStatus ? required(name) : null
  }
  const schema: Schema<IncomeDetailsForm> = {
    employmentStatus: getDropdownFieldSchema({
      id: 'employment-status-dropdown',
      label: t('consumerLending:affordability.employmentStatus.label'),
      options: {
        ...required('employmentStatus'),
      },
      items: employmentStatusList,
    }),
    incomeFrequency: getDropdownFieldSchema({
      id: 'income-frequency-dropdown',
      label: t('consumerLending:affordability.incomeFrequency.label'),
      options: { ...isRequired('incomeFrequency') },
      items: incomeFrequencyList,
      show: showOtherIncomeFields,
    }),
    netIncome: getMoneyFieldSchema({
      label: frequency
        ? t('consumerLending:affordability.netIncome.label', { frequency: frequency.toLowerCase() })
        : t('consumerLending:affordability.netIncome.defaultLabel'),
      options: { ...isRequired('netIncome'), ...checkIncome(true) },
      show: showOtherIncomeFields,
      currencySymbol: symbol,
    }),
    otherIncome: getMoneyFieldSchema({
      label: t(`consumerLending:affordability.otherIncome.label`, { frequency: frequency?.toLowerCase() }),
      options: {
        ...checkIncome(),
      },
      otherProps: {
        icon: 'Info',
        iconClickHandler: handleIconClicked,
        iconRef: otherIncomeRef,
      },
      show: isValidStatus,
      currencySymbol: symbol,
      ariaDescribedBy: 'other-income-tooltip',
    }),
    changeInCircumstancesText: {
      type: FieldType.Component,
      Component: components.changeInCircumstancesText,
      show: showOtherIncomeFields,
    },
    changeInCircumstances: {
      type: FieldType.Component,
      Component: components.changeInCircumstances,
      show: showOtherIncomeFields,
    },
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }
  return { schema }
}
